import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import type { ThemeColors } from '../../../contexts/ThemeContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { Button } from '../../../components/ui/Button';
import { PlusIcon, TrashIcon } from '../../../components/ui/Icons';
import { cn } from '../../../lib/utils';

const EXCEL_TEMPLATE_MAPPING_VERSION = 1;

export type MappingEditorHandle = {
  /** Builds version 1 mapping for PUT /mapping. Returns null if nothing is mapped. */
  buildMapping: () => Record<string, unknown> | null;
};

type ScalarRow = { sheet: string; cell: string };

type TableForm = {
  id: string;
  sheet: string;
  firstDataRow: number;
  rowFillMode: 'overwrite';
  sourceKey: string;
  columns: { fieldId: string; colLetter: string }[];
};

type Props = {
  allowedScalarFields: string[];
  allowedLineFields: string[];
  /** Current mapping from GET …/mapping (null or partial). */
  existingMapping: Record<string, unknown> | null | undefined;
  disabled?: boolean;
};

function humanScalarLabel(field: string): string {
  const map: Record<string, string> = {
    vesselName: 'Vessel name',
    portName: 'Port',
    enquiryRef: 'Enquiry ref',
    enqDate: 'Enquiry date',
    categoryName: 'Category',
    reqRef: 'Requisition ref',
    reqDate: 'Requisition date',
    notes: 'Notes',
    quoteRef: 'Quote ref',
    quoteDate: 'Quote date',
    supplierName: 'Supplier',
    evalRef: 'Evaluation ref',
    evalDate: 'Evaluation date',
    poRef: 'PO ref',
    poDate: 'PO date',
    deliveryRef: 'Delivery ref',
    deliveryDate: 'Delivery date',
    invoiceRef: 'Invoice ref',
    invoiceDate: 'Invoice date',
  };
  return map[field] ?? field;
}

function humanLineLabel(field: string): string {
  const map: Record<string, string> = {
    lineNo: 'Line #',
    itemCode: 'Item code',
    description: 'Description',
    unit: 'Unit',
    qty: 'Qty',
    rob: 'ROB',
    notes: 'Notes',
    enquiryLineId: 'Enquiry line ID',
    leadTimeDays: 'Lead time (days)',
    unitPrice: 'Unit price',
    lineAmount: 'Line amount',
  };
  return map[field] ?? field;
}

function deserialize(
  existing: Record<string, unknown> | null | undefined,
  allowedScalars: string[],
): {
  scalars: Record<string, ScalarRow>;
  tables: TableForm[];
} {
  const scalars: Record<string, ScalarRow> = {};
  for (const k of allowedScalars) {
    scalars[k] = { sheet: '', cell: '' };
  }

  if (!existing || typeof existing !== 'object') {
    return {
      scalars,
      tables: [
        {
          id: 'exportLines',
          sheet: '',
          firstDataRow: 20,
          rowFillMode: 'overwrite',
          sourceKey: 'lines',
          columns: [],
        },
      ],
    };
  }

  const o = existing as {
    scalars?: Record<string, { sheet?: string; cell?: string }>;
    tables?: Array<{
      id?: string;
      sheet?: string;
      firstDataRow?: number;
      rowFillMode?: string;
      sourceKey?: string;
      columns?: Record<string, string>;
    }>;
  };

  for (const k of allowedScalars) {
    const b = o.scalars?.[k];
    scalars[k] = {
      sheet: b?.sheet ?? '',
      cell: b?.cell ?? '',
    };
  }

  const tables: TableForm[] = (o.tables ?? []).map((t) => ({
    id: t.id ?? 'exportLines',
    sheet: t.sheet ?? '',
    firstDataRow: typeof t.firstDataRow === 'number' && t.firstDataRow > 0 ? t.firstDataRow : 20,
    rowFillMode: 'overwrite',
    sourceKey: t.sourceKey ?? 'lines',
    columns: Object.entries(t.columns ?? {}).map(([fieldId, colLetter]) => ({
      fieldId,
      colLetter,
    })),
  }));

  if (tables.length === 0) {
    tables.push({
      id: 'exportLines',
      sheet: '',
      firstDataRow: 20,
      rowFillMode: 'overwrite',
      sourceKey: 'lines',
      columns: [],
    });
  }

  return { scalars, tables };
}

function inputClass(colors: ThemeColors) {
  return cn(
    'w-full rounded-lg border px-2 py-1.5 text-sm',
    colors.border,
    colors.surfaceHover,
    colors.text,
  );
}

export const MappingEditor = forwardRef<MappingEditorHandle, Props>(function MappingEditor(
  { allowedScalarFields, allowedLineFields, existingMapping, disabled },
  ref,
) {
  const { colors } = useTheme();
  const [scalars, setScalars] = useState<Record<string, ScalarRow>>({});
  const [tables, setTables] = useState<TableForm[]>([]);

  const scalarKeys = useMemo(() => [...allowedScalarFields].sort(), [allowedScalarFields]);

  useEffect(() => {
    const { scalars: s, tables: t } = deserialize(existingMapping, allowedScalarFields);
    setScalars(s);
    setTables(t);
  }, [existingMapping, allowedScalarFields]);

  useImperativeHandle(
    ref,
    () => ({
      buildMapping: () => {
        const outScalars: Record<string, { sheet: string; cell: string }> = {};
        for (const k of scalarKeys) {
          const row = scalars[k];
          if (!row) continue;
          const sheet = row.sheet.trim();
          const cell = row.cell.trim();
          if (sheet && cell) {
            outScalars[k] = { sheet, cell };
          }
        }

        const outTables = tables
          .map((tb) => {
            const sheet = tb.sheet.trim();
            const cols: Record<string, string> = {};
            for (const c of tb.columns) {
              const f = c.fieldId.trim();
              const letter = c.colLetter.trim().toUpperCase();
              if (f && letter && /^[A-Z]+$/.test(letter)) {
                cols[f] = letter;
              }
            }
            return {
              id: tb.id.trim() || 'exportLines',
              sheet,
              firstDataRow: tb.firstDataRow,
              rowFillMode: 'overwrite' as const,
              sourceKey: tb.sourceKey.trim() || 'lines',
              columns: cols,
            };
          })
          .filter((tb) => tb.sheet && tb.firstDataRow > 0 && Object.keys(tb.columns).length > 0);

        const hasScalars = Object.keys(outScalars).length > 0;
        const hasTables = outTables.length > 0;
        if (!hasScalars && !hasTables) {
          return null;
        }

        return {
          version: EXCEL_TEMPLATE_MAPPING_VERSION,
          scalars: outScalars,
          tables: outTables,
        };
      },
    }),
    [scalarKeys, scalars, tables],
  );

  const updateScalar = (key: string, field: keyof ScalarRow, value: string) => {
    setScalars((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const updateTable = (idx: number, patch: Partial<TableForm>) => {
    setTables((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };

  const addTable = () => {
    setTables((prev) => [
      ...prev,
      {
        id: `table${prev.length + 1}`,
        sheet: '',
        firstDataRow: 20,
        rowFillMode: 'overwrite',
        sourceKey: 'lines',
        columns: [],
      },
    ]);
  };

  const removeTable = (idx: number) => {
    setTables((prev) => prev.filter((_, i) => i !== idx));
  };

  const addColumn = (tableIdx: number) => {
    setTables((prev) =>
      prev.map((t, i) =>
        i === tableIdx ? { ...t, columns: [...t.columns, { fieldId: '', colLetter: '' }] } : t,
      ),
    );
  };

  const updateColumn = (
    tableIdx: number,
    colIdx: number,
    patch: Partial<{ fieldId: string; colLetter: string }>,
  ) => {
    setTables((prev) =>
      prev.map((t, i) => {
        if (i !== tableIdx) return t;
        const cols = t.columns.map((c, j) => (j === colIdx ? { ...c, ...patch } : c));
        return { ...t, columns: cols };
      }),
    );
  };

  const removeColumn = (tableIdx: number, colIdx: number) => {
    setTables((prev) =>
      prev.map((t, i) => {
        if (i !== tableIdx) return t;
        return { ...t, columns: t.columns.filter((_, j) => j !== colIdx) };
      }),
    );
  };

  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <h3 className={cn('text-sm font-semibold', colors.text)}>Header fields (single cells)</h3>
        <p className={cn('text-xs', colors.textSecondary)}>
          Enter the worksheet name and cell (e.g. B9) for each value. Leave blank to skip.
        </p>
        <div className={cn('rounded-lg border overflow-x-auto', colors.border)}>
          <table className="min-w-full text-sm">
            <thead className={cn(colors.surfaceHover)}>
              <tr>
                <th className="text-left px-3 py-2 font-medium">Field</th>
                <th className="text-left px-3 py-2 font-medium">Sheet</th>
                <th className="text-left px-3 py-2 font-medium w-28">Cell</th>
              </tr>
            </thead>
            <tbody>
              {scalarKeys.map((key) => (
                <tr key={key} className={cn('border-t', colors.border)}>
                  <td className="px-3 py-2 align-middle">{humanScalarLabel(key)}</td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      className={inputClass(colors)}
                      value={scalars[key]?.sheet ?? ''}
                      onChange={(e) => updateScalar(key, 'sheet', e.target.value)}
                      placeholder="REQ"
                      disabled={disabled}
                      autoComplete="off"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      className={inputClass(colors)}
                      value={scalars[key]?.cell ?? ''}
                      onChange={(e) => updateScalar(key, 'cell', e.target.value)}
                      placeholder="B9"
                      disabled={disabled}
                      autoComplete="off"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className={cn('text-sm font-semibold', colors.text)}>Line tables</h3>
            <p className={cn('text-xs mt-0.5', colors.textSecondary)}>
              Maps enquiry lines onto rows starting at <strong>First data row</strong>. Columns are
              letters only (A, B, …). Use source key <code className="text-xs">lines</code> for RFQ
              exports.
            </p>
          </div>
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={addTable}
            disabled={disabled}
            title="Add line table"
            aria-label="Add line table"
          >
            <PlusIcon className="w-4 h-4" />
          </Button>
        </div>

        <div className="space-y-4">
          {tables.map((tb, ti) => (
            <div
              key={`${tb.id}-${ti}`}
              className={cn('rounded-xl border p-4 space-y-3', colors.border, colors.surfaceHover)}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <span className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>
                  Table {ti + 1}
                </span>
                {tables.length > 1 && (
                  <Button
                    type="button"
                    size="icon"
                    variant="danger"
                    onClick={() => removeTable(ti)}
                    disabled={disabled}
                    title="Remove table"
                    aria-label="Remove table"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <label className="flex flex-col gap-1">
                  <span className={cn('text-xs', colors.textSecondary)}>Table id</span>
                  <input
                    type="text"
                    className={inputClass(colors)}
                    value={tb.id}
                    onChange={(e) => updateTable(ti, { id: e.target.value })}
                    disabled={disabled}
                    autoComplete="off"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className={cn('text-xs', colors.textSecondary)}>Sheet</span>
                  <input
                    type="text"
                    className={inputClass(colors)}
                    value={tb.sheet}
                    onChange={(e) => updateTable(ti, { sheet: e.target.value })}
                    placeholder="REQ"
                    disabled={disabled}
                    autoComplete="off"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className={cn('text-xs', colors.textSecondary)}>First data row (1-based)</span>
                  <input
                    type="number"
                    min={1}
                    className={inputClass(colors)}
                    value={tb.firstDataRow}
                    onChange={(e) => updateTable(ti, { firstDataRow: Math.max(1, Number(e.target.value) || 1) })}
                    disabled={disabled}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className={cn('text-xs', colors.textSecondary)}>Source key</span>
                  <input
                    type="text"
                    className={inputClass(colors)}
                    value={tb.sourceKey}
                    onChange={(e) => updateTable(ti, { sourceKey: e.target.value })}
                    placeholder="lines"
                    disabled={disabled}
                    autoComplete="off"
                  />
                </label>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className={cn('text-xs font-medium', colors.textSecondary)}>Columns</span>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    onClick={() => addColumn(ti)}
                    disabled={disabled}
                    title="Add column"
                    aria-label="Add column"
                  >
                    <PlusIcon className="w-4 h-4" />
                  </Button>
                </div>
                {tb.columns.length === 0 && (
                  <p className={cn('text-xs italic', colors.textMuted)}>No columns — add at least one.</p>
                )}
                <div className="space-y-2">
                  {tb.columns.length > 0 && (
                    <div
                      className={cn(
                        'hidden sm:grid grid-cols-[minmax(0,1fr)_5rem_2.5rem] gap-2 items-center text-[10px] font-semibold uppercase tracking-wide px-0.5',
                        colors.textMuted,
                      )}
                    >
                      <span>Field</span>
                      <span className="text-center">Col</span>
                      <span className="sr-only">Remove</span>
                    </div>
                  )}
                  {tb.columns.map((col, ci) => (
                    <div
                      key={ci}
                      className="grid grid-cols-[minmax(0,1fr)_5rem_2.5rem] gap-2 items-center w-full min-w-0"
                    >
                      <select
                        className={cn(inputClass(colors), 'min-w-0 w-full')}
                        value={col.fieldId}
                        onChange={(e) => updateColumn(ti, ci, { fieldId: e.target.value })}
                        disabled={disabled}
                      >
                        <option value="">— Field —</option>
                        {allowedLineFields.map((f) => (
                          <option key={f} value={f}>
                            {humanLineLabel(f)}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        className={cn(inputClass(colors), 'w-full text-center tabular-nums')}
                        value={col.colLetter}
                        onChange={(e) => updateColumn(ti, ci, { colLetter: e.target.value })}
                        placeholder="A"
                        disabled={disabled}
                        autoComplete="off"
                        aria-label="Excel column letter"
                      />
                      <div className="flex justify-center">
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={() => removeColumn(ti, ci)}
                          disabled={disabled}
                          title="Remove column"
                          aria-label="Remove column"
                        >
                          <TrashIcon className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
});
